import { V2 } from '../../theme/tokens'
import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { formatNumber } from '../../utils/format';

/**
 * Relation type colors for visual consistency
 */
export const RELATION_COLORS = {
  complement: V2.okText,
  addon: '#0ea5e9',
  competitor: V2.errText,
};

/**
 * Relation type labels for display
 */
export const RELATION_LABELS = {
  complement: 'Complement',
  addon: 'Add-on',
  competitor: 'Competitor',
};

/**
 * Sort options
 */
const SORT_OPTIONS = [
  { value: 'score', label: 'Score' },
  { value: 'name', label: 'Name' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'type', label: 'Product Type' },
];

/**
 * Default filter values
 */
export const DEFAULT_FILTERS = {
  relationTypes: ['complement', 'addon', 'competitor'],
  minScore: 0,
  sortBy: 'score',
  sortOrder: 'desc',
  hideDecided: false, // When true, hide items that have been approved/excluded
};

/**
 * Shared filter controls component for recommendations views.
 *
 * @param {Object} props
 * @param {string[]} props.relationTypes - Selected relation types
 * @param {Function} props.onRelationTypesChange - Handler for relation type changes
 * @param {number} props.minScore - Minimum score threshold (0-1)
 * @param {Function} props.onMinScoreChange - Handler for min score changes
 * @param {string} props.sortBy - Current sort field
 * @param {Function} props.onSortByChange - Handler for sort field changes
 * @param {string} props.sortOrder - 'asc' or 'desc'
 * @param {Function} props.onSortOrderChange - Handler for sort order changes
 * @param {Function} props.onReset - Handler for resetting to defaults
 * @param {boolean} props.compact - Show compact layout
 */
export default function FilterControls({
  // Individual props pattern
  relationTypes: propRelationTypes,
  onRelationTypesChange,
  minScore: propMinScore,
  onMinScoreChange,
  sortBy: propSortBy,
  onSortByChange,
  sortOrder: propSortOrder,
  onSortOrderChange,
  onReset,
  // Object pattern (alternative)
  filters,
  onChange,
  // Layout options
  compact = false,
  showRelationTypes = true,
}) {
  // Support both individual props and filters object
  const relationTypes = propRelationTypes ?? filters?.relationTypes ?? DEFAULT_FILTERS.relationTypes;
  const minScore = propMinScore ?? filters?.minScore ?? DEFAULT_FILTERS.minScore;
  const sortBy = propSortBy ?? filters?.sortBy ?? DEFAULT_FILTERS.sortBy;
  const sortOrder = propSortOrder ?? filters?.sortOrder ?? DEFAULT_FILTERS.sortOrder;
  const hideDecided = filters?.hideDecided ?? DEFAULT_FILTERS.hideDecided;

  // Unified change handlers
  const handleChange = (key, value) => {
    if (onChange) {
      onChange({ [key]: value });
    }
  };
  const handleRelationToggle = (type) => {
    let newTypes;
    if (relationTypes.includes(type)) {
      // Don't allow deselecting all
      if (relationTypes.length > 1) {
        newTypes = relationTypes.filter(t => t !== type);
      } else {
        return;
      }
    } else {
      newTypes = [...relationTypes, type];
    }

    if (onRelationTypesChange) {
      onRelationTypesChange(newTypes);
    }
    handleChange('relationTypes', newTypes);
  };

  const handleMinScoreChange = (_, value) => {
    if (onMinScoreChange) {
      onMinScoreChange(value);
    }
    handleChange('minScore', value);
  };

  const handleSortByChange = (event) => {
    const value = event.target.value;
    if (onSortByChange) {
      onSortByChange(value);
    }
    handleChange('sortBy', value);
  };

  const handleSortOrderToggle = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    if (onSortOrderChange) {
      onSortOrderChange(newOrder);
    }
    handleChange('sortOrder', newOrder);
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else if (onChange) {
      onChange(DEFAULT_FILTERS);
    } else {
      // Apply defaults individually
      onRelationTypesChange?.(DEFAULT_FILTERS.relationTypes);
      onMinScoreChange?.(DEFAULT_FILTERS.minScore);
      onSortByChange?.(DEFAULT_FILTERS.sortBy);
      onSortOrderChange?.(DEFAULT_FILTERS.sortOrder);
    }
  };

  return (
    <Stack
      direction={compact ? 'column' : { xs: 'column', md: 'row' }}
      spacing={2}
      alignItems={compact ? 'stretch' : { xs: 'stretch', md: 'center' }}
      flexWrap="wrap"
    >
      {/* Relation type chips */}
      {showRelationTypes && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          {Object.entries(RELATION_LABELS).map(([type, label]) => (
            <Chip
              key={type}
              label={label}
              clickable
              color={relationTypes.includes(type) ? 'primary' : 'default'}
              variant={relationTypes.includes(type) ? 'filled' : 'outlined'}
              onClick={() => handleRelationToggle(type)}
              sx={{
                borderColor: RELATION_COLORS[type],
                '&.MuiChip-filled': {
                  backgroundColor: RELATION_COLORS[type],
                },
              }}
            />
          ))}
        </Stack>
      )}

      {/* Min score slider */}
      <Box sx={{ minWidth: compact ? 'auto' : 200, px: 1 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Min Score: {formatNumber(minScore)}
        </Typography>
        <Slider
          value={minScore}
          onChange={handleMinScoreChange}
          min={0}
          max={1}
          step={0.05}
          size="small"
          valueLabelDisplay="auto"
          sx={{ mt: 0.5 }}
        />
      </Box>

      {/* Sort controls */}
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            onChange={handleSortByChange}
            label="Sort by"
          >
            {SORT_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title={sortOrder === 'desc' ? 'Descending' : 'Ascending'}>
          <Chip
            label={sortOrder === 'desc' ? 'DESC' : 'ASC'}
            size="small"
            clickable
            onClick={handleSortOrderToggle}
            variant="outlined"
          />
        </Tooltip>
      </Stack>

      {/* Hide decided toggle - shows only undecided items (to-do list mode) */}
      <Tooltip title={hideDecided ? "Show all recommendations" : "Hide approved/excluded items (to-do mode)"}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideDecided}
              onChange={(e) => handleChange('hideDecided', e.target.checked)}
              color="primary"
            />
          }
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <ChecklistIcon fontSize="small" />
              <Typography variant="body2">To-do</Typography>
            </Stack>
          }
          sx={{ ml: 0 }}
        />
      </Tooltip>

      {/* Reset button */}
      <Tooltip title="Reset filters">
        <Button
          size="small"
          variant="text"
          color="inherit"
          onClick={handleReset}
          startIcon={<RestartAltIcon />}
          sx={{ minWidth: 'auto' }}
        >
          Reset
        </Button>
      </Tooltip>
    </Stack>
  );
}
